// HMAC-SHA-256 tokens via Web Crypto. Copied from Kettle (packages/server-core/src/tokens.ts,
// 2026-10-20) with the issuer made a parameter; see docs/kettle-sharing.md before changing it.
const enc = new TextEncoder();

export function b64url(bytes: ArrayBuffer | Uint8Array): string {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = "";
  for (const x of b) s += String.fromCharCode(x);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function b64urlDecode(s: string): Uint8Array<ArrayBuffer> {
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/") + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

const keyCache = new Map<string, Promise<CryptoKey>>();
function hmacKey(secret: string): Promise<CryptoKey> {
  let k = keyCache.get(secret);
  if (!k) {
    k = crypto.subtle.importKey("raw", enc.encode(secret), { name: "HMAC", hash: "SHA-256" }, false, ["sign", "verify"]);
    keyCache.set(secret, k);
  }
  return k;
}

export async function sign(secret: string, data: string): Promise<string> {
  return b64url(await crypto.subtle.sign("HMAC", await hmacKey(secret), enc.encode(data)));
}

export async function verify(secret: string, data: string, sig: string): Promise<boolean> {
  try {
    return await crypto.subtle.verify("HMAC", await hmacKey(secret), b64urlDecode(sig), enc.encode(data));
  } catch {
    return false;
  }
}

/** Compact signed token: b64url(json) "." sig. Payload must contain `exp` (ms since epoch). */
export async function makeToken(secret: string, payload: Record<string, unknown>): Promise<string> {
  const body = b64url(enc.encode(JSON.stringify(payload)));
  return `${body}.${await sign(secret, body)}`;
}

export async function readToken<T extends { exp: number }>(secret: string, token: string, now = Date.now()): Promise<T | null> {
  const [body, sig] = token.split(".");
  if (!body || !sig || !(await verify(secret, body, sig))) return null;
  try {
    const p = JSON.parse(new TextDecoder().decode(b64urlDecode(body))) as T;
    return typeof p.exp === "number" && p.exp > now ? p : null;
  } catch {
    return null;
  }
}

/** JWT HS256 access token. */
export async function makeJwt(secret: string, kid: string, claims: Record<string, unknown>): Promise<string> {
  const header = b64url(enc.encode(JSON.stringify({ alg: "HS256", typ: "JWT", kid })));
  const body = b64url(enc.encode(JSON.stringify(claims)));
  return `${header}.${body}.${await sign(secret, `${header}.${body}`)}`;
}

/** Default issuer of Estra access tokens. */
export const JWT_ISSUER = "estra";

/** Verifies an access JWT: HS256 with this secret, from `issuer` for `audience` (the app id), not expired. */
export async function readJwt(secret: string, jwt: string, audience: string, now = Date.now(), issuer = JWT_ISSUER): Promise<{ sub: string; exp: number } | null> {
  const [h, b, s] = jwt.split(".");
  if (!h || !b || !s || !(await verify(secret, `${h}.${b}`, s))) return null;
  try {
    const header = JSON.parse(new TextDecoder().decode(b64urlDecode(h))) as { alg?: string; typ?: string };
    const c = JSON.parse(new TextDecoder().decode(b64urlDecode(b))) as { sub: string; exp: number; aud?: string; iss?: string };
    if (header.alg !== "HS256" || c.iss !== issuer || c.aud !== audience) return null;
    return typeof c.sub === "string" && c.exp * 1000 > now ? c : null;
  } catch {
    return null;
  }
}

export async function sha256Hex(s: string): Promise<string> {
  const d = await crypto.subtle.digest("SHA-256", enc.encode(s));
  return [...new Uint8Array(d)].map((x) => x.toString(16).padStart(2, "0")).join("");
}

export function randomId(bytes = 16): string {
  return b64url(crypto.getRandomValues(new Uint8Array(bytes)));
}

/** Like readJwt, but returns every claim (custom claims feed security rules). Estra-only addition. */
export async function readJwtClaims(secret: string, jwt: string, audience: string, now = Date.now(), issuer = JWT_ISSUER): Promise<Record<string, unknown> & { sub: string; exp: number } | null> {
  if (!(await readJwt(secret, jwt, audience, now, issuer))) return null;
  try {
    return JSON.parse(new TextDecoder().decode(b64urlDecode(jwt.split(".")[1]!)));
  } catch {
    return null;
  }
}
